/**
 * Draft persistence manager.
 *
 * Encapsulates the storage, serialization, debounced writes and restore logic
 * previously embedded in {@link MdyFormEngine}. The form engine owns one
 * instance and delegates `enableDraft` / `clearDraft` to it.
 */
import { reactivityRunsEffects } from "./reactivity-contract.js";
import { isSafeFieldPath } from "./path-utils.js";
import { MDY_DEV } from "./dev-flags.js";
import { isRecord } from "./record-utils.js";
import { MDY_DRAFT_KEY_IN_USE, MDY_DRAFT_NOT_RESTORED } from "./reactivity-diagnostics.js";
function isDraftEnvelope(parsed) {
    if (!isRecord(parsed))
        return false;
    const draft = parsed["__mdyDraft"];
    const value = parsed["value"];
    return typeof draft === "number" && isRecord(value);
}
/**
 * How far ahead of this clock a stamp may be and still be believed.
 *
 * Two machines rarely agree to the millisecond, and a draft written on one and read on another is
 * the ordinary case — so a stamp a little in the future is a clock, not a claim. Far in the future is
 * a claim, and the only thing it can buy is an expiry that never arrives.
 */
const MDY_DRAFT_CLOCK_SKEW_MS = 5 * 60_000;
/**
 * Whether an envelope is young enough to restore.
 *
 * `ttlMs` asks *how old is this*, and the answer comes from the envelope, which is written where
 * anything on the origin can write. A stamp that is missing, is not a number, or sits further ahead
 * than a clock can explain answers nothing — and a question with no answer is not answered with the
 * one that opens: an expiry a draft can opt out of by lying is not an expiry.
 */
function withinAge(savedAt, ttlMs) {
    if (typeof savedAt !== "number" || !Number.isFinite(savedAt))
        return false;
    const age = Date.now() - savedAt;
    if (age < -MDY_DRAFT_CLOCK_SKEW_MS)
        return false;
    return age <= ttlMs;
}
/**
 * The storage this manager will use, whichever of the two shapes it was handed.
 *
 * The guide names `localStorage` as the default, so the object a reader reaches for is the platform's
 * own — and the option took a different shape without saying so: the first read threw
 * `this._storage.read is not a function`, which names a private field, from a stack inside the
 * engine, about an argument the caller passed. A shape this package can adapt is adapted; a shape it
 * cannot is refused where it arrives, naming what was expected.
 */
function asDraftStorage(given) {
    const candidate = given;
    if (typeof candidate.read === "function"
        && typeof candidate.write === "function"
        && typeof candidate.remove === "function") {
        return given;
    }
    if (typeof candidate.getItem === "function"
        && typeof candidate.setItem === "function"
        && typeof candidate.removeItem === "function") {
        const web = given;
        return {
            // Bound to the object they came from: a Web Storage method called detached throws
            // `Illegal invocation`, which is the same failure one layer further in.
            read: (key) => web.getItem(key),
            write: (key, value) => web.setItem(key, value),
            remove: (key) => web.removeItem(key),
        };
    }
    throw new Error("[modyra] draft.storage must be { read, write, remove } — or a Web Storage such as "
        + "`localStorage`, which is taken as it is. Received an object with: "
        + `${Object.keys(candidate).length > 0 ? Object.keys(candidate).join(", ") : "no usable members"}.`);
}
/**
 * Default browser storage — inert when `localStorage` is unavailable or
 * blocked (SSR, Node, sandboxed iframes, browsers that throw SecurityError
 * on access when cookies/site data are disabled).
 */
function localStorageDraftStorage() {
    let available = false;
    try {
        available = typeof localStorage !== "undefined" && localStorage !== null;
    }
    catch {
        // Accessing `localStorage` itself throws in restrictive modes.
    }
    return {
        read: (key) => {
            if (!available)
                return null;
            try {
                return localStorage.getItem(key);
            }
            catch {
                return null;
            }
        },
        write: (key, value) => {
            if (available)
                localStorage.setItem(key, value);
        },
        remove: (key) => {
            if (!available)
                return;
            try {
                localStorage.removeItem(key);
            }
            catch {
                // Ignore: nothing to clean up if the storage is unreachable.
            }
        },
    };
}
/**
 * True for values that must never enter a draft: binary payloads
 * (`File`/`Blob`/`FileList`) and `BigInt`, whose JSON round-trip would
 * silently change the restored type.
 */
function isDraftUnsafeLeaf(value) {
    if (typeof value === "bigint")
        return true;
    if (typeof Blob !== "undefined" && value instanceof Blob)
        return true; // File extends Blob
    if (typeof FileList !== "undefined" && value instanceof FileList)
        return true;
    return false;
}
/**
 * True when the value is (or contains) a draft-unsafe leaf (see above).
 *
 * Walked over an explicit stack, because this is one of the checks a **stored** draft has to pass
 * and a stored draft is untrusted input: `localStorage` is writable by any script on the origin.
 * `JSON.parse` reads a deeply nested document without difficulty, so the value arrives whole and a
 * recursive check ran one frame per level until the stack ended — the guard failing on exactly the
 * input it exists to check. The throw escaped `createForm`, so an application got no form at all,
 * on every load, until someone cleared the key.
 */
function containsFile(value) {
    if (value === null)
        return false;
    if (isDraftUnsafeLeaf(value))
        return true;
    if (typeof value !== "object")
        return false;
    const seen = new WeakSet();
    const pending = [value];
    while (pending.length > 0) {
        const held = pending.pop();
        if (held === null)
            continue;
        if (isDraftUnsafeLeaf(held))
            return true;
        if (typeof held !== "object")
            continue;
        if (seen.has(held))
            continue;
        seen.add(held);
        if (Array.isArray(held)) {
            pending.push(...held);
            continue;
        }
        // Keys rather than values: `Object.values` *reads* every member, and a getter that throws would
        // take the write down — and with it the form, from inside a debounce nobody is awaiting. This
        // walk decides whether a value may be stored; a member it cannot read is a member it cannot
        // store either, so an unreadable one answers the question the same way a File does.
        let keys;
        try {
            keys = Object.keys(held);
        }
        catch {
            return true;
        }
        for (const key of keys) {
            try {
                pending.push(held[key]);
            }
            catch {
                return true;
            }
        }
    }
    return false;
}
/**
 * Manages draft persistence for a single form instance.
 */
/**
 * Whether a draft entry is one the form said never to persist.
 *
 * `exclude` began as a set of exact leaf paths, which answers for a field at the top of a form and
 * for nothing else. The guide's own example is a card number, card numbers live in a list, and a row
 * key is data — so the only spelling that worked, `cards.a.pan`, is the one nobody can write before
 * the user has added the row. A consumer following the instruction correctly still persisted the
 * secret, and everything about the form afterwards looked right.
 *
 * Four spellings answer now, and the reason they all do is that this is a promise about a secret:
 * an entry excluded by mistake is a convenience lost, and an entry persisted by mistake is a card
 * number in plain text that survives a logout. The direction to be generous in is not in question.
 *
 * - the exact path, as before
 * - an ancestor: `cards` excludes everything under `cards.`
 * - a pattern: `*` stands for exactly one segment, so `cards.*.pan` is that cell in every row
 * - a bare name: `pan` — no dot — excludes any cell called `pan`, wherever it is
 */
function draftPathExcluded(path, patterns) {
    if (patterns.size === 0)
        return false;
    const segments = path.split(".");
    for (const pattern of patterns) {
        if (pattern === path)
            return true;
        if (path.startsWith(`${pattern}.`))
            return true;
        if (!pattern.includes(".")) {
            if (segments[segments.length - 1] === pattern)
                return true;
            continue;
        }
        if (!pattern.includes("*"))
            continue;
        const wanted = pattern.split(".");
        // A pattern may name a subtree as well as a leaf: `cards.*` covers `cards.a.pan`.
        if (wanted.length > segments.length)
            continue;
        if (wanted.every((segment, index) => segment === "*" || segment === segments[index]))
            return true;
    }
    return false;
}
/**
 * Whether `path` is one the schema declared a secret, or sits under one.
 *
 * Exact paths and subtrees only — never the bare-name match `exclude` allows. `exclude` is a list a
 * consumer writes, where naming a leaf and meaning "wherever it is" is a convenience; a declaration
 * is a fact about **one** field. Read the loose way, a root `token` marked secret withheld a row's
 * unrelated `token`, and a row's `answer` was protected only when some other field happened to share
 * its name — a value withheld by coincidence, and an ordinary column disappearing from a restored
 * draft because a field elsewhere was a secret.
 */
function declaredSecret(path, secrets) {
    if (secrets.size === 0)
        return false;
    if (secrets.has(path))
        return true;
    for (const secret of secrets) {
        if (path.startsWith(`${secret}.`))
            return true;
    }
    return false;
}
export class MdyDraftManager {
    _rx;
    _getValue;
    _patchValue;
    _hasDraft;
    _warn;
    /** The stamp this form last wrote, so a stamp it did not write is recognisable as another writer's. */
    _lastStamp = null;
    _filterRestoredEntry;
    _scope;
    _isDeactivated;
    /** Which form this is, as a short name for its shape — see {@link DraftManagerDeps.formShape}. */
    _formShape;
    /** Config from enableDraft(), recorded but not yet started (deactivated at the time). */
    _pendingOptions = null;
    /**
     * What the form held when a deferred start was recorded.
     *
     * The baseline is "what the user has not changed", and for a form that starts with its own
     * construction that is its initial value. A form built deactivated starts later, and taking the
     * baseline then made everything written in between part of it: a form hydrated from a payload in
     * the tick it was built kept nothing until the user typed, while a form that paused and resumed
     * wrote on resuming. React and Preact construct with `autoActivate: false`, so that was the common
     * case rather than the exotic one.
     */
    _deferredBaseline = null;
    /** True once `_start()` has run at least once — resume() then just restarts the effect. */
    _hasStarted = false;
    /** True once this form has refused to replace a draft describing a form that is not it. */
    _refusedForeign = false;
    _key = null;
    _storage = null;
    _effect = null;
    _timer = null;
    _exclude = new Set();
    /**
     * The paths the schema calls secrets, asked for on every write rather than copied once.
     *
     * A collection's rows are data: a row created after the draft was enabled declares its cells then,
     * so a set snapshotted at enable time knows about none of them — and a secret in a row is the one
     * a consumer could never have named in `exclude`, because its path did not exist yet.
     */
    _secretPaths = () => new Set();
    _version = 1;
    _debounceMs = 400;
    /** Serialized value at enable time — a pristine form writes no draft. */
    _baseline = null;
    _lastWritten = null;
    constructor(deps) {
        this._rx = deps.rx;
        this._getValue = deps.getValue;
        this._patchValue = deps.patchValue;
        this._hasDraft = deps.hasDraft;
        this._warn = deps.warn;
        this._filterRestoredEntry = deps.filterRestoredEntry;
        if (deps.secretPaths)
            this._secretPaths = deps.secretPaths;
        this._isDeactivated = deps.isDeactivated;
        this._formShape = deps.formShape;
        this._scope = deps.scope;
    }
    /**
     * Persists the form value under `key` on every (debounced) change and
     * restores an existing draft immediately. The draft is cleared
     * automatically after a submit that reports no errors, or manually via
     * {@link clearDraft}. `File` values are skipped (not serializable).
     */
    enableDraft(options) {
        if (this._effect || this._pendingOptions)
            return;
        if (!reactivityRunsEffects(this._rx)) {
            if (MDY_DEV)
                this._warn("enableDraft() needs an effect-capable reactivity " +
                    "— see your reactivity adapter for how to provide one.");
            return;
        }
        if (this._isDeactivated()) {
            // Construction must stay pure and storage-free until activation
            // — record the config, do nothing else yet. The value is read, which
            // touches no storage: it is the baseline the start would have taken had it happened here.
            this._pendingOptions = options;
            this._deferredBaseline = this._serialize(this._rx.untracked(() => this._getValue())) ?? null;
            return;
        }
        this._start(options);
    }
    _start(options) {
        this._key = options.key;
        this._storage = options.storage === undefined
            ? localStorageDraftStorage()
            : asDraftStorage(options.storage);
        this._exclude = new Set(options.exclude ?? []);
        this._version = options.version ?? 1;
        this._debounceMs = options.debounceMs ?? 400;
        // Restore an existing draft before recording starts.
        // A storage that refuses to be read is a draft that is not there. Safari in private browsing
        // throws on access, an enterprise policy throws, a blocked third-party context throws — and a
        // draft is an optional convenience, so failing to read one means there is no draft, never that
        // there is no form. The write side has always been swallowed for the same reason.
        let stored = null;
        try {
            stored = this._storage.read(this._key);
        }
        catch (error) {
            if (MDY_DEV) {
                this._warn(`Draft storage could not be read (${error instanceof Error ? error.message : String(error)}). ` +
                    "The form opens without a draft.");
            }
        }
        // Another form's work is neither restored nor removed. `_parse` answering `null` means the entry
        // is unusable — corrupt, superseded, expired — and the caller drops it; an envelope belonging to
        // another form is perfectly usable, by that form, and this one has no standing to delete it.
        if (stored !== null && this._belongsToAnotherForm(stored)) {
            // Said out loud. Skipping in silence left a consumer unable to tell a key holding nothing from
            // a key holding work this form declined to read — and the second is somebody's typing, still
            // on disk, that nothing will ever offer them again unless the form that wrote it comes back.
            this._warn(`A draft under "${this._key}" was written by a form of a different shape and has not been ` +
                "restored. It is left where it is: the form that wrote it can still read it. A form whose " +
                "own shape moved — a field added, a collection row arriving from a server — is a different " +
                "form by this measure, so bump `version` in the draft options when that is intended.", MDY_DRAFT_NOT_RESTORED);
            stored = null;
        }
        if (stored !== null) {
            const value = this._parse(stored, options.ttlMs);
            if (value !== null) {
                // Stored drafts are untrusted input: drop excluded keys, any
                // reserved/empty path segment (__proto__ etc.) — instead of letting
                // field creation throw mid-restore — and entries that fail the
                // engine's shape validation (tampered storage).
                this._patchValue(Object.fromEntries(Object.entries(value).filter(([k, v]) => !draftPathExcluded(k, this._exclude) &&
                    !declaredSecret(k, this._secretPaths()) &&
                    isSafeFieldPath(k) &&
                    (this._filterRestoredEntry?.(k, v) ?? true))));
                this._hasDraft.set(true);
                this._lastWritten = this._serialize(value) ?? null;
            }
            else {
                this._storage.remove(this._key);
            }
        }
        this._baseline = this._deferredBaseline ?? this._serialize(this._rx.untracked(() => this._getValue())) ?? null;
        this._deferredBaseline = null;
        this._hasStarted = true;
        this._startEffect();
    }
    _startEffect() {
        this._effect = this._rx.effect((onCleanup) => {
            const current = this._getValue();
            this._rx.untracked(() => {
                if (this._timer !== null)
                    clearTimeout(this._timer);
                this._timer = setTimeout(() => {
                    this._timer = null;
                    this._write(current);
                }, this._debounceMs);
            });
            onCleanup(() => {
                if (this._timer !== null) {
                    clearTimeout(this._timer);
                    this._timer = null;
                }
            });
        }, { scope: this._scope, debugName: "modyra:draft" });
    }
    /**
     * Stops the write effect and its pending debounce timer without losing
     * any state (key/storage/baseline/hasDraft) — `resume()` restarts it
     * exactly where it left off. Called by the owning form's `deactivate()`.
     */
    pause() {
        this._effect?.destroy();
        this._effect = null;
        if (this._timer !== null) {
            clearTimeout(this._timer);
            this._timer = null;
        }
    }
    /**
     * Restarts what `pause()` stopped, or performs the deferred first start
     * if `enableDraft()` was called while deactivated. A no-op if draft was
     * never enabled, or is already running. Called by the owning form's
     * `activate()`.
     */
    resume() {
        if (this._effect)
            return;
        if (this._pendingOptions) {
            const options = this._pendingOptions;
            this._pendingOptions = null;
            this._start(options);
            return;
        }
        if (this._hasStarted)
            this._startEffect();
    }
    /** Removes the stored draft (also called after an error-free submit). */
    clearDraft() {
        if (this._key && this._storage) {
            // Same rule as reading it: a storage that will not remove an entry is not the caller's problem
            // to catch. What `clearDraft` promises about the *form* — the baseline moves, the draft is no
            // longer offered — holds whether or not the entry could be deleted.
            try {
                this._storage.remove(this._key);
            }
            catch (error) {
                if (MDY_DEV) {
                    this._warn(`Draft storage could not remove "${this._key}" ` +
                        `(${error instanceof Error ? error.message : String(error)}). The form has discarded it either way.`);
                }
            }
        }
        this._hasDraft.set(false);
        this._lastWritten = null;
        // The current (submitted) value becomes the new baseline.
        this._baseline = this._serialize(this._rx.untracked(() => this._getValue())) ?? null;
    }
    /** Releases timers, effects, this form's claim on its draft key, and resets the hasDraft signal. */
    destroy() {
        if (this._timer !== null) {
            clearTimeout(this._timer);
            this._timer = null;
        }
        this._effect?.destroy();
        this._effect = null;
        this._hasDraft.set(false);
    }
    /**
     * Whether a stored envelope records a form shape that is not this form's.
     *
     * The same question the write side asks, on the way back in. A draft records the shape of the form
     * that wrote it so two forms sharing a key can tell their work apart, and the writer already
     * refuses to overwrite an envelope carrying another shape — so the draft it declined to replace was
     * the one this restored. What one person typed in one form arrived filled in in another, and was
     * submitted from there; no storage was tampered with, and both envelopes were written here.
     *
     * A draft that records no shape is this form's own earlier work as far as anything can tell:
     * refusing it would discard what a person typed to close a hole that draft cannot be on either side
     * of. The path comparison the write side makes is what covers those.
     */
    _belongsToAnotherForm(stored) {
        if (this._formShape === undefined)
            return false;
        let shape;
        try {
            shape = JSON.parse(stored).shape;
        }
        catch {
            return false;
        }
        return typeof shape === "string" && shape !== this._formShape();
    }
    /**
     * Parses a stored draft, returning its value or `null` when it must be
     * discarded (corrupt JSON, version mismatch, expired TTL). Envelope-less
     * payloads written by pre-versioning releases are still accepted.
     */
    _parse(stored, ttlMs) {
        try {
            const parsed = JSON.parse(stored);
            if (isDraftEnvelope(parsed)) {
                if (parsed.__mdyDraft !== this._version)
                    return null;
                if (ttlMs !== undefined && !withinAge(parsed.savedAt, ttlMs))
                    return null;
                return parsed.value;
            }
            if (isRecord(parsed)) {
                return parsed; // legacy plain draft
            }
            return null;
        }
        catch {
            return null;
        }
    }
    _serialize(value) {
        const serializable = Object.fromEntries(Object.entries(value).filter(([k, v]) => !draftPathExcluded(k, this._exclude)
            && !declaredSecret(k, this._secretPaths())
            && !containsFile(v)));
        const seen = new WeakSet();
        try {
            return JSON.stringify(serializable, (_key, raw) => {
                // BigInt-bearing fields are filtered out before serialization; one
                // surfacing here (e.g. via toJSON) must skip the write, not mutate
                // the restored type to string.
                if (typeof raw === "bigint") {
                    throw new TypeError("BigInt is not draft-serializable");
                }
                if (typeof raw === "object" && raw !== null) {
                    if (seen.has(raw)) {
                        throw new TypeError("Circular reference");
                    }
                    seen.add(raw);
                }
                return raw;
            });
        }
        catch {
            if (MDY_DEV)
                this._warn("Skipped draft write: value is not JSON-serializable (cycle or unsupported type).");
            return null;
        }
    }
    /**
     * What is in storage now, read back before a write replaces it.
     *
     * A draft key identifies the *form*, not the window — that is what makes a draft survive a reload —
     * so two tabs of one form share it by design. The engine defined the envelope, stamps `savedAt` on
     * every save and is the only thing that reads one, and it was writing that stamp without ever
     * comparing it: a tab that had been open a while replaced a draft another view had saved a minute
     * later, and stamped the replacement with the earlier time. The one field a later reader could use
     * to notice said the opposite.
     *
     * Returns the stamp of whatever is there, or null when there is nothing readable — an absent key,
     * a storage that raises, a payload from another writer. None of those is a conflict.
     */
    _storedStamp() {
        if (!this._key || !this._storage)
            return null;
        try {
            const raw = this._storage.read(this._key);
            if (typeof raw !== "string" || raw.length === 0)
                return null;
            const parsed = JSON.parse(raw);
            return typeof parsed.savedAt === "number" && Number.isFinite(parsed.savedAt)
                ? parsed.savedAt
                : null;
        }
        catch {
            return null;
        }
    }
    /**
     * The paths of a stored envelope this form does not declare.
     *
     * A draft key names one form's work. Two live forms sharing a key is not exotic — a component
     * rendered twice, a route mounting a form beside another, a key copied along with the options it
     * sits in — and the last save takes the whole envelope: one person's typing is gone from the only
     * place it was kept, and reopening their form restores nothing, because the draft under their key
     * now describes fields they do not have.
     *
     * The shape is what tells the two apart. A form re-opened on its own draft, or a second tab of the
     * same form, stores paths this form declares, and replacing that is the intended behaviour. Paths
     * it cannot read back belong to a different form.
     */
    _foreignPaths() {
        if (!this._key || !this._storage || !this._filterRestoredEntry)
            return [];
        let raw = null;
        try {
            raw = this._storage.read(this._key);
        }
        catch {
            return [];
        }
        if (typeof raw !== "string" || raw.length === 0)
            return [];
        let value;
        try {
            value = JSON.parse(raw).value;
        }
        catch {
            return [];
        }
        // A shape the writer recorded and this form does not have: another form's work, whatever its
        // paths look like from here. A draft written before shapes were recorded carries none, and falls
        // through to the path comparison below.
        const storedShape = (() => {
            try {
                const parsed = JSON.parse(raw);
                return typeof parsed.shape === "string" ? parsed.shape : null;
            }
            catch {
                return null;
            }
        })();
        const mine = this._formShape?.();
        if (storedShape !== null && mine !== undefined && storedShape !== mine) {
            return Object.keys(isRecord(value) ? value : {}).slice(0, 3);
        }
        if (!isRecord(value))
            return [];
        return Object.keys(value).filter((path) => !draftPathExcluded(path, this._exclude) &&
            isSafeFieldPath(path) &&
            !this._filterRestoredEntry(path, value[path]));
    }
    _write(value) {
        if (!this._key || !this._storage)
            return;
        const serialized = this._serialize(value);
        if (serialized === null)
            return;
        // Nothing the user changed → no draft; unchanged → no rewrite.
        if (serialized === this._lastWritten)
            return;
        if (this._lastWritten === null && serialized === this._baseline) {
            return;
        }
        // A draft describing a form that is not this one is another form's work, and replacing it loses
        // it: what is left behind is a draft neither form can read back. This form keeps no draft of its
        // own rather than take the key, and says so once — a person's every keystroke is not a new
        // event.
        const foreign = this._foreignPaths();
        if (foreign.length > 0) {
            if (!this._refusedForeign) {
                this._refusedForeign = true;
                this._warn(`A draft under "${this._key}" holds ${foreign.slice(0, 3).map((path) => `"${path}"`).join(", ")}` +
                    `${foreign.length > 3 ? ", …" : ""}, which this form does not declare, so it belongs to another ` +
                    "form. This form keeps no draft: saving would replace work nothing could read back. Give each " +
                    "form its own key.", MDY_DRAFT_KEY_IN_USE);
            }
            return;
        }
        const now = Date.now();
        const stored = this._storedStamp();
        // Someone else wrote since this form last did. The typing in front of the person wins — a draft
        // is a convenience and throwing away what they are writing to keep what they are not is the
        // worse answer — but it is said out loud, and the stamp never goes backwards: the record of when
        // the stored draft was written is the only thing a later reader has.
        const replacing = stored !== null && this._lastStamp !== null && stored > this._lastStamp;
        if (replacing && MDY_DEV) {
            this._warn(`A draft under "${this._key}" was saved more recently by something else and has been ` +
                "replaced. A draft key names the form, so two views of it share one — give each view its " +
                "own key if they must not overwrite each other.");
        }
        // Never backwards, and never further ahead than a clock explains. Carrying a stored stamp
        // forward is what stops a draft being replaced by one saved earlier; carrying an *impossible*
        // one forward makes it permanent — every later save inherits it, and an expiry the user can
        // never reach however long they keep typing.
        const believable = stored !== null && stored <= now + MDY_DRAFT_CLOCK_SKEW_MS;
        const savedAt = believable && stored > now ? stored : now;
        // Build the envelope around the already-serialized payload so the value
        // is stringified only once per write.
        const shape = this._formShape?.();
        const envelope = `{"__mdyDraft":${this._version}${shape === undefined ? "" : `,"shape":${JSON.stringify(shape)}`},"savedAt":${savedAt},"value":${serialized}}`;
        try {
            this._storage.write(this._key, envelope);
            this._lastWritten = serialized;
            this._lastStamp = savedAt;
        }
        catch {
            // Quota errors and private-mode restrictions must not break the form.
        }
    }
}
